import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { PageHeader, Modal, DetailModal, ConfirmDialog, Spinner, EmptyState, useToast, usePagination, Pagination, useSort, SortTh, SearchableSelect, InlineSpinner, TableSkeletonRows } from '../components/UI';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate, fmtDateTime, fmtPct } from '../utils/format';
import FileAttachments from '../components/FileAttachments';

const EMPTY = { student_id:'', enrolment_id:'', module_code:'', subject:'', assessment:'', score:'', max_score:100, grade:'', assessment_date:'', remarks:'' };
const GRADES = ['A+','A','A-','B+','B','B-','C+','C','C-','D','F'];
const blockDecimalKeys = e => { if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); };

export default function AcademicPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [searchParams] = useSearchParams();
  const [rows,     setRows]     = useState([]);
  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [show,     setShow]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [delId,    setDelId]    = useState(null);
  const [filterStu,setFilterStu]= useState('');
  const [viewing,  setViewing]  = useState(null);
  const [enrolments,   setEnrolments]   = useState([]);
  const [enrolLoading, setEnrolLoading] = useState(false);
  const [modules,      setModules]      = useState([]);
  const [modLoading,   setModLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'assessment' ? 'mcq' : 'academic'); // 'academic' | 'mcq'
  const [mcqRows,   setMcqRows]   = useState([]);
  const [mcqLoading,setMcqLoading]= useState(false);
  const [mcqDetail, setMcqDetail] = useState(null);
  const [mcqDetailLoading, setMcqDetailLoading] = useState(false);
  const [mcqWrongOnly, setMcqWrongOnly] = useState(false);
  const [mcqYear,   setMcqYear]   = useState(String(new Date().getFullYear()));
  const [mcqIntake, setMcqIntake] = useState('');
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    const params = {};
    if (filterStu) params.student_id = filterStu;
    api.get('/academic', { params }).then(r => setRows(r.data.data))
      .catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };

  useEffect(() => { api.get('/students').then(r => setStudents(r.data.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [filterStu]);

  const mcqIsAll = !filterStu;

  useEffect(() => {
    if (activeTab !== 'mcq') return;
    setMcqLoading(true);
    const url = mcqIsAll ? '/academic/mcq' : `/academic/mcq/${filterStu}`;
    api.get(url).then(r => setMcqRows(r.data.data))
      .catch(() => setMcqRows([])).finally(() => setMcqLoading(false));
  }, [activeTab, filterStu, mcqIsAll]);

  const openMcqDetail = r => {
    setMcqDetailLoading(true);
    setMcqWrongOnly(false);
    const studentId = r.student_no || filterStu;
    const studentName = r.student_name || students.find(s => s.student_no === studentId)?.name;
    setMcqDetail({ assessment_name: r.assessment_name, type: r.type, student_no: studentId, student_name: studentName });
    api.get(`/academic/mcq/${studentId}/${r.assessment_id}/${r.user_id}/${r.attempt}`)
      .then(res => setMcqDetail({ ...res.data.data, student_no: studentId, student_name: studentName }))
      .catch(() => { toast('Failed to load attempt details','error'); setMcqDetail(null); })
      .finally(() => setMcqDetailLoading(false));
  };

  const fmtDuration = secs => {
    if (secs === null || secs === undefined) return '—';
    const m = Math.floor(secs / 60), s = secs % 60;
    return m > 0 ? `${m} min ${s} sec` : `${s} sec`;
  };

  // Loads a student's enrolments (student_courses) so Add/Edit Record can offer
  // an Intake picker; module choices then come from that specific enrolment's
  // student_modules via loadModules below, once an intake is picked.
  const loadEnrolments = studentNo => {
    setModules([]);
    if (!studentNo) { setEnrolments([]); return; }
    setEnrolLoading(true);
    api.get(`/students/${studentNo}/courses`).then(r => setEnrolments(r.data.data))
      .catch(() => setEnrolments([])).finally(() => setEnrolLoading(false));
  };

  const loadModules = (studentNo, enrolmentId) => {
    setModLoading(true);
    api.get(`/students/${studentNo}/courses/${enrolmentId}/modules`).then(r => setModules(r.data.data))
      .catch(() => setModules([])).finally(() => setModLoading(false));
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setEnrolments([]); setModules([]); setError(''); setFieldErrors({}); setShow(true); };

  // Unlike loadEnrolments (fire-and-forget, used when the user picks a
  // student), editing needs the enrolment/module lists in hand *before* it
  // can resolve the record's saved intake_code/module_code back to the
  // enrolment_id the Intake select needs — so this awaits both fetches
  // instead of firing them independently.
  const openEdit = async r => {
    setEditing(r);
    setForm({ ...r, enrolment_id: '', assessment_date: r.assessment_date?.slice(0,10)||'' });
    setEnrolments([]); setModules([]);
    setError(''); setFieldErrors({}); setShow(true);
    if (!r.student_id) return;
    setEnrolLoading(true);
    try {
      const enrRes = await api.get(`/students/${r.student_id}/courses`);
      setEnrolments(enrRes.data.data);
      const match = r.intake_code && enrRes.data.data.find(e => e.intake_code === r.intake_code);
      if (match) {
        setForm(f => ({ ...f, enrolment_id: match.id }));
        setModLoading(true);
        try {
          const modRes = await api.get(`/students/${r.student_id}/courses/${match.id}/modules`);
          setModules(modRes.data.data);
        } catch { setModules([]); }
        finally { setModLoading(false); }
      }
    } catch { setEnrolments([]); }
    finally { setEnrolLoading(false); }
  };

  const selectStudent = studentNo => {
    setForm(f => ({ ...f, student_id: studentNo, enrolment_id: '', module_code: '', subject: '' }));
    loadEnrolments(studentNo);
  };

  const selectIntake = enrolmentId => {
    const enr = enrolments.find(e => String(e.id) === String(enrolmentId));
    setForm(f => ({ ...f, enrolment_id: enrolmentId, intake_code: enr?.intake_code || '', module_code: '', subject: '' }));
    setModules([]);
    if (enr) loadModules(form.student_id, enrolmentId);
  };

  const selectModule = moduleCode => {
    const mod = modules.find(m => m.module_code === moduleCode);
    setForm(f => ({ ...f, module_code: moduleCode, subject: mod?.module_name || '' }));
  };

  const validateForm = () => {
    const errs = {};
    if (!form.student_id)          errs.student_id      = 'Student is required.';
    if (!form.enrolment_id)        errs.enrolment_id    = 'Intake is required.';
    if (!form.module_code || !form.subject) errs.module_code = 'Module is required.';
    if (!form.assessment || !form.assessment.trim()) errs.assessment    = 'Assessment is required.';
    if (form.score === '' || form.score === null || form.score === undefined) errs.score = 'Score is required.';
    if (form.max_score === '' || form.max_score === null || form.max_score === undefined) errs.max_score = 'Max score is required.';
    if (!errs.score && !errs.max_score && Number(form.score) > Number(form.max_score))
      errs.score = 'Score cannot be greater than max score.';
    if (!form.assessment_date)     errs.assessment_date = 'Assessment date is required.';
    return errs;
  };

  const handleSave = async e => {
    e.preventDefault(); setError('');
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError('Please fill all required fields.');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/academic/${editing.id}`, form);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/academic', form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Record ${editing ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/academic/${delId}`); toast('Record deleted','success'); setDelId(null); load(); }
    catch { toast('Delete failed','error'); }
  };

  const filtered = rows.filter(r =>
    (r.student_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.student_id||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.subject||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.assessment||'').toLowerCase().includes(search.toLowerCase())
  );


  const { sorted: acadSorted, sortKey: acadSK, sortDir: acadSD, toggleSort: acadSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(acadSorted);

  // Year is derived from submitted_at (when the attempt was actually made),
  // not stored — filtered on but not shown as its own column. The MCQ
  // system's earliest records go back to 2024, so the picker always offers
  // 2024..this year regardless of what's actually in the current result set.
  const currentYear = new Date().getFullYear();
  const mcqYears = [];
  for (let y = currentYear; y >= 2024; y--) mcqYears.push(y);
  const mcqIntakeOptions = [...new Set(mcqRows.map(r => r.intake_code).filter(Boolean))].sort();

  const mcqFiltered = mcqRows.filter(r => {
    const matchesSearch = (r.assessment_name||'').toLowerCase().includes(search.toLowerCase()) ||
      (r.student_name||'').toLowerCase().includes(search.toLowerCase()) ||
      (r.student_no||'').toLowerCase().includes(search.toLowerCase());
    const matchesYear = !mcqYear || (r.submitted_at && String(new Date(r.submitted_at).getFullYear()) === mcqYear);
    const matchesIntake = !mcqIntake || r.intake_code === mcqIntake;
    return matchesSearch && matchesYear && matchesIntake;
  });
  const { sorted: mcqSorted, sortKey: mcqSK, sortDir: mcqSD, toggleSort: mcqSort } = useSort(mcqFiltered);
  const { paged: mcqPaged, page: mcqPage, setPage: mcqSetPage, pageSize: mcqPageSize, setPageSize: mcqSetPageSize,
    totalPages: mcqTotalPages, total: mcqTotal, start: mcqStart } = usePagination(mcqSorted);

  const getPctColor = pct => pct >= 75 ? 'var(--sms-dot-present)' : pct >= 50 ? 'var(--sms-dot-late)' : 'var(--sms-dot-absent)';

  return (
    <>
      <PageHeader title="Academic Performance" subtitle={activeTab === 'academic' ? `${total} records` : `${mcqTotal} Assessment Results`}
        actions={activeTab === 'academic' && can('academic_performance','create') && (
          <button className="sms-btn sms-btn-primary" onClick={openCreate}>
            <i className="bi bi-plus-lg" /> Add Record
          </button>
        )} />

      <div className="sms-tabs" role="tablist" aria-label="Academic performance views">
        <button role="tab" aria-selected={activeTab === 'academic'} className={`sms-tab${activeTab === 'academic' ? ' active' : ''}`}
          onClick={() => { setActiveTab('academic'); setSearch(''); setFilterStu(''); }}>
          <i className="bi bi-graph-up" /> Academic Records
        </button>
        <button role="tab" aria-selected={activeTab === 'mcq'} className={`sms-tab${activeTab === 'mcq' ? ' active' : ''}`}
          onClick={() => { setActiveTab('mcq'); setSearch(''); setFilterStu(''); }}>
          <i className="bi bi-clipboard-check" /> Assessment Result
        </button>
      </div>

      <div className="sms-card mb-3" style={{padding:'12px 14px'}}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-5" style={{position:'relative'}}>
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>Search</label>
            <i className="bi bi-search" style={{position:'absolute',left:9,top:28,color:'var(--sms-txt3)',fontSize:13}}/>
            <input className="sms-form-control" style={{paddingLeft:28}}
              placeholder={activeTab === 'academic' ? 'Student, subject, assessment…' : 'Student, assessment…'}
              value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div className="col-6 col-md-4">
            <label style={{fontSize: 12,color:'var(--sms-txt2)',display:'block',marginBottom:4}}>Student</label>
            <SearchableSelect value={filterStu} onChange={e=>setFilterStu(e.target.value)} placeholder="All students"
              options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} />
          </div>
          <div className="col-6 col-md-3">
            <button className="sms-btn sms-btn-outline-danger" onClick={()=>{setSearch('');setFilterStu('');}}><i className="bi bi-x-circle"/> Clear</button>
          </div>
        </div>
      </div>

      {activeTab === 'mcq' && (
        <div className="sms-card mb-3" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--sms-bdr)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>MCQ Assessment Results{mcqIsAll ? ' — All Students' : ''}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 2 }}>Year</label>
                <select className="sms-form-control" style={{ fontSize: 12.5, padding: '5px 8px' }}
                  value={mcqYear} onChange={e => setMcqYear(e.target.value)}>
                  <option value="">All years</option>
                  {mcqYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--sms-txt3)', display: 'block', marginBottom: 2 }}>Intake</label>
                <select className="sms-form-control" style={{ fontSize: 12.5, padding: '5px 8px' }}
                  value={mcqIntake} onChange={e => setMcqIntake(e.target.value)}>
                  <option value="">All intakes</option>
                  {mcqIntakeOptions.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
          </div>
          {!mcqLoading && mcqFiltered.length === 0 ? (
            <EmptyState icon="bi-clipboard-check" message={mcqRows.length === 0 ? 'No Assessment Results found' : 'No Assessment Results match the selected filters'} />
          ) : (
            <>
            <div style={{ overflowX: 'auto' }}>
              <table className="sms-table">
                <thead>
                  <tr>
                    {mcqIsAll && <SortTh label="Student" field="student_name" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>}
                    <SortTh label="Assessment" field="assessment_name" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>
                    <th style={{ fontSize: 12 }}>Intake</th>
                    <SortTh label="Type" field="type" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>
                    <SortTh label="Score" field="correct_count" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>
                    <SortTh label="%" field="percentage" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>
                    <SortTh label="Submitted" field="submitted_at" sortKey={mcqSK} sortDir={mcqSD} onSort={mcqSort}/>
                  </tr>
                </thead>
                <tbody>
                  {mcqLoading ? <TableSkeletonRows columns={mcqIsAll ? 7 : 6} /> : mcqPaged.map(r => (
                    <tr key={`${r.assessment_id}-${r.user_id}`} style={{ cursor: 'pointer' }} onClick={() => openMcqDetail(r)}>
                      {mcqIsAll && (
                        <td>
                          <div style={{ fontWeight: 500 }}>{r.student_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{r.student_no}</div>
                        </td>
                      )}
                      <td>{r.assessment_name}</td>
                      <td>{r.intake_code || '—'}</td>
                      <td>
                        <span className={`badge rounded-pill ${r.type === 'Exam' ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>
                          {r.type}
                        </span>
                      </td>
                      <td>{r.correct_count}/{r.max_score}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)', borderRadius: 3, overflow: 'hidden', minWidth: 40 }}>
                            <div style={{ height: '100%', width: `${r.percentage}%`, background: getPctColor(r.percentage), borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 500, color: getPctColor(r.percentage), minWidth: 34 }}>
                            {fmtPct(r.percentage)}
                          </span>
                        </div>
                      </td>
                      <td>{fmtDateTime(r.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!mcqLoading && <Pagination page={mcqPage} totalPages={mcqTotalPages} total={mcqTotal} pageSize={mcqPageSize}
              setPage={mcqSetPage} setPageSize={mcqSetPageSize} start={mcqStart} idSuffix="-mcq" />}
            </>
          )}
        </div>
      )}

      {activeTab === 'academic' && (
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-graph-up" message="No academic records found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <SortTh label="Student ID" field="student_id" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Student Name" field="student_name" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Intake" field="intake_code" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Assessment" field="assessment" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Score" field="score" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="%" field="percentage" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Grade" field="grade" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  <SortTh label="Date" field="assessment_date" sortKey={acadSK} sortDir={acadSD} onSort={acadSort}/>
                  {(can('academic_performance','update') || can('academic_performance','delete')) && <th style={{ width: 90 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8 + ((can('academic_performance','update') || can('academic_performance','delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.id} onClick={() => setViewing(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight: 500 }}>{r.student_name}</td>
                    <td>{r.intake_code || '—'}</td>
                    <td>{r.assessment}</td>
                    <td>{r.score}/{r.max_score}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 6, background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)', borderRadius: 3, overflow: 'hidden', minWidth: 40 }}>
                          <div style={{ height: '100%', width: `${r.percentage}%`, background: getPctColor(r.percentage), borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 500, color: getPctColor(r.percentage), minWidth: 34 }}>
                          {fmtPct(r.percentage)}
                        </span>
                      </div>
                    </td>
                    <td>
                      {r.grade && (
                        <span className="badge rounded-pill badge-active" style={{ fontSize: 12 }}>{r.grade}</span>
                      )}
                    </td>
                    <td>{fmtDate(r.assessment_date)}</td>
                    {(can('academic_performance','update') || can('academic_performance','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {can('academic_performance','update') && (
                            <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>
                          )}
                          {can('academic_performance','delete') && (
                            <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-stat-red)' }} onClick={() => setDelId(r.id)}><i className="bi bi-trash" /></button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start}/>}
          </>
        )}
      </div>
      )}

      <Modal show={show} onClose={() => setShow(false)} title={editing ? 'Edit Record' : 'Add Academic Record'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.student_id ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e => selectStudent(e.target.value)} placeholder="-- Select student --"
                disabled={!!editing}
                options={students.map(s => ({ value: s.student_no, label: s.name, right: s.student_no }))} style={fieldErrors.student_id ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.student_id && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.student_id}</div>}
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.enrolment_id ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake *</label>
              <SearchableSelect value={form.enrolment_id} onChange={e => selectIntake(e.target.value)}
                placeholder={!form.student_id ? 'Select a student first' : enrolLoading ? 'Loading…' : '-- Select intake --'}
                disabled={!form.student_id || enrolLoading}
                options={enrolments.map(e => ({ value: e.id, label: e.intake_code }))}
                style={fieldErrors.enrolment_id ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.enrolment_id && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.enrolment_id}</div>}
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.module_code ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module *</label>
              <SearchableSelect value={form.module_code} onChange={e => selectModule(e.target.value)}
                placeholder={!form.enrolment_id ? 'Select an intake first' : modLoading ? 'Loading…' : '-- Select module --'}
                disabled={!form.enrolment_id || modLoading}
                options={modules.map(m => ({ value: m.module_code, label: m.module_name }))}
                style={fieldErrors.module_code ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.module_code && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.module_code}</div>}
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.assessment ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Assessment *</label>
              <input className="sms-form-control" value={form.assessment || ''} onChange={e => setForm(f => ({ ...f, assessment: e.target.value }))}
                style={fieldErrors.assessment ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.assessment && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.assessment}</div>}
            </div>
            <div className="col-6 col-sm-3">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.score ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Score *</label>
              <input type="number" className="sms-form-control" value={form.score} step="1" min="0"
                onKeyDown={blockDecimalKeys}
                onChange={e => setForm(f => ({ ...f, score: e.target.value.replace(/[^\d]/g, '') }))}
                style={fieldErrors.score ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.score && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.score}</div>}
            </div>
            <div className="col-6 col-sm-3">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.max_score ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Max Score *</label>
              <input type="number" className="sms-form-control" value={form.max_score} step="1" min="1"
                onKeyDown={blockDecimalKeys}
                onChange={e => setForm(f => ({ ...f, max_score: e.target.value.replace(/[^\d]/g, '') }))}
                style={fieldErrors.max_score ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.max_score && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.max_score}</div>}
            </div>
            <div className="col-6 col-sm-3">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Grade</label>
              <SearchableSelect value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} placeholder="-- Grade --"
                options={GRADES.map(g => ({ value: g, label: g }))} />
            </div>
            <div className="col-6 col-sm-3">
              <label style={{ fontSize: 12, fontWeight: 500, color: fieldErrors.assessment_date ? '#b91c1c' : 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Date *</label>
              <input type="date" className="sms-form-control" value={form.assessment_date} onChange={e => setForm(f => ({ ...f, assessment_date: e.target.value }))}
                style={fieldErrors.assessment_date ? { borderColor: '#b91c1c' } : {}} />
              {fieldErrors.assessment_date && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 3 }}>{fieldErrors.assessment_date}</div>}
            </div>
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Remarks</label>
              <textarea className="sms-form-control" rows={2} value={form.remarks || ''} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="academic_performance" recordId={editing?.id ?? null}
              canUpload={can('academic_performance','create')||can('academic_performance','update')} canDelete={can('academic_performance','delete')} />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}>Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <><InlineSpinner style={{ marginRight: 4 }} />Saving…</> : (editing ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog show={!!delId} danger message="Delete this academic record?" onConfirm={handleDelete} onCancel={() => setDelId(null)} />
      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Academic Record Details" size="lg" fields={viewing ? [
        { label: 'Student ID',      value: viewing.student_id },
        { label: 'Student Name',    value: viewing.student_name },
        { label: 'Intake',          value: viewing.intake_code || '—' },
        { label: 'Subject',         value: viewing.subject },
        { label: 'Assessment',      value: viewing.assessment },
        { label: 'Score',           value: `${viewing.score}/${viewing.max_score}` },
        { label: 'Percentage',      value: `${fmtPct(viewing.percentage)}` },
        { label: 'Grade',           value: viewing.grade },
        { label: 'Assessment Date', value: fmtDate(viewing.assessment_date) },
        { label: 'Recorded By',     value: viewing.recorded_by_name },
        { label: 'Remarks',         value: viewing.remarks, full: true },
      ] : []}>
        {viewing && (
          <FileAttachments module="academic_performance" recordId={viewing.id} canUpload={false} canDelete={false} />
        )}
      </DetailModal>

      <Modal show={!!mcqDetail} onClose={() => setMcqDetail(null)} title={
        <div>
          <div>{mcqDetail?.assessment_name || 'Attempt Details'}</div>
          {mcqDetail?.student_name && (
            <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 2 }}>
              {mcqDetail.student_name} <span style={{  }}>({mcqDetail.student_no})</span>
            </div>
          )}
        </div>
      } size="lg">
        {mcqDetailLoading ? <Spinner minHeight="200px" /> : !mcqDetail?.questions ? (
          <div style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>Failed to load attempt details.</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'var(--sms-sur2)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Score</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{mcqDetail.correct_count}/{mcqDetail.max_score}</div>
              </div>
              <div style={{ background: 'var(--sms-sur2)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Percentage</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: getPctColor(mcqDetail.percentage) }}>{fmtPct(mcqDetail.percentage)}</div>
              </div>
              <div style={{ background: 'var(--sms-sur2)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Wrong</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--sms-dot-absent)' }}>{mcqDetail.max_score - mcqDetail.correct_count}</div>
              </div>
              <div style={{ background: 'var(--sms-sur2)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Submitted</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDateTime(mcqDetail.submitted_at)}</div>
              </div>
              <div style={{ background: 'var(--sms-sur2)', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Duration</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDuration(mcqDetail.duration_seconds)}</div>
              </div>
            </div>

            {mcqDetail.max_score - mcqDetail.correct_count > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 10, cursor: 'pointer', width: 'fit-content' }}>
                <input type="checkbox" checked={mcqWrongOnly} onChange={e => setMcqWrongOnly(e.target.checked)} />
                Show wrong answers only
              </label>
            )}

            <div style={{ maxHeight: 420, overflowY: 'auto', borderTop: '1px solid var(--sms-bdr)' }}>
              {mcqDetail.questions.filter(q => !mcqWrongOnly || !q.is_correct).map((q, i) => (
                <div key={q.question_id} className={q.is_correct ? 'mcq-q-correct' : 'mcq-q-wrong'}
                  style={{ padding: '10px 12px', marginTop: 6, borderBottom: '1px solid var(--sms-bdr)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 13 }}>{i + 1}. {q.question_text}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }} title="Time spent on this question">
                        <i className="bi bi-clock" /> {q.time_spent_seconds === null ? '—' : fmtDuration(q.time_spent_seconds)}
                      </span>
                      <span className={`badge rounded-pill ${q.is_correct ? 'badge-present' : 'badge-absent'}`}
                        style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', height: 'fit-content' }}>
                        {q.is_correct ? 'Correct' : 'Wrong'}
                      </span>
                    </div>
                  </div>
                  {q.is_correct ? (
                    <div style={{ fontSize: 12, color: 'var(--sms-dot-present)' }}>
                      <i className="bi bi-check-lg" /> {q.chosen_answer ?? '(no answer)'}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--sms-dot-absent)', fontWeight: 500 }}>
                        <i className="bi bi-x-lg" /> {q.chosen_answer ?? '(no answer)'} (their answer)
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sms-dot-present)', fontWeight: 500 }}>
                        <i className="bi bi-check-lg" /> {q.correct_answer ?? '—'} (correct answer)
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
